import React, { useState, useEffect } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { AiOutlineArrowLeft, AiOutlineCheckCircle, AiOutlineExclamationCircle } from 'react-icons/ai';
import apiClient from "../utils/apiClient";
import countryData from "./countryData";
import { useServerRole } from "../utils/permissions";
import {
  allowUser,
  rejectUser,
  requestingusers,
  requesttosell,
  bulkUpdateUserStatus,
} from "../utils/APIRoutes";
import './UserRequests.css';

// Support/admin contact id — used only as a link TARGET (where to
// route a "contact support" click), not as a permission check. Real
// admin gating now comes from useServerRole() below, which asks the
// backend directly rather than comparing ids in the browser.
const ADMIN_USER_ID = process.env.REACT_APP_ADMIN_ID;

const UserRequests = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [selectedCountry, setSelectedCountry] = useState("");
  const [phoneCode, setPhoneCode] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [address, setAddress] = useState("");
  const [requestedUsers, setRequestedUsers] = useState([]);
  const [countrySearch, setCountrySearch] = useState("");
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);
  const [filteredCountries, setFilteredCountries] = useState([]);
  const [hasRequested, setHasRequested] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState(null);
  const [userSearch, setUserSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkActionLoading, setBulkActionLoading] = useState(false);

  // SECURITY: this asks the backend "what can the CURRENTLY
  // AUTHENTICATED session actually do" — it doesn't trust the `userId`
  // route param (which, in this app's flow, is usually the caller's
  // own id anyway, but there's no reason to lean on that when the
  // server can just tell us directly, and it stays correct even
  // immediately after a role is granted/revoked without a re-login).
  const { isAdmin, hasPermission, loading: roleLoading } = useServerRole();

  useEffect(() => {
    const userHasRequested = requestedUsers.some(user => user._id === userId);
    setHasRequested(userHasRequested);
  }, [userId, requestedUsers]);

  // Filter countries based on search term
  useEffect(() => {
    if (countrySearch.trim() === '') {
      setFilteredCountries([]);
      return;
    }

    const results = countryData.filter(country =>
      country.name.toLowerCase().includes(countrySearch.toLowerCase())
    ).slice(0, 10); // Limit to 10 results

    setFilteredCountries(results);
  }, [countrySearch]);

  const handleCountrySelect = (country) => {
    setSelectedCountry(country.name);
    setPhoneCode(country.phoneCode);
    setCountrySearch(country.name);
    setShowCountryDropdown(false);
  };

  // Fetch requested users
  const fetchRequestedUsers = async (
  search = "",
  page = 1
) => {
  try {

    const { data } = await apiClient.get(
      `${requestingusers}?search=${encodeURIComponent(
        search
      )}&page=${page}&limit=20`
    );

    /* ------------------------------
       BACKWARD COMPATIBILITY
    ------------------------------ */

    const users = Array.isArray(data)
      ? data
      : Array.isArray(data.users)
      ? data.users
      : [];

    /* ------------------------------
       NORMALIZE USERS
    ------------------------------ */

    const normalizedUsers =
      users.map((user) => ({
        _id: user._id,

        username:
          user.username || "Unknown User",

        email:
          user.email || "",

        avatarImage:
          user.avatarImage || "",

        isAvatarImageSet:
          user.isAvatarImageSet || false,

        phoneNumber:
          user.phoneNumber || "",

        address:
          user.address || "",

        country:
          user.country || "",

        allowed:
          Boolean(user.allowed),

        requested:
          Boolean(user.requested),

        subscription:
          user.subscription || {
            type: "none",
            active: false,
          },

        profileCompleteness:
          user.profileCompleteness || 0,

        lastActive:
          user.lastActive || null,
      }));

    setRequestedUsers(
      normalizedUsers
    );
    setSelectedIds([]); // selection doesn't survive a refresh of the list

  } catch (error) {
    // A 401/403 here just means this viewer isn't an admin — the
    // interceptor in apiClient handles 401 globally; 403 (logged in,
    // but not admin) is expected for non-admin users landing on this
    // component and isn't worth surfacing as an error.
    if (error.response?.status !== 401 && error.response?.status !== 403) {
      console.error("Error fetching requested users:", error);
    }
  }
};

  useEffect(() => {
    if (!isAdmin) return; // don't even try — this list is admin-only server-side anyway

    const delayDebounce = setTimeout(() => {
      fetchRequestedUsers(userSearch, 1);
    }, 400);

    return () => clearTimeout(delayDebounce);
  }, [userSearch, isAdmin]);


 // Handle allow user
const handleAllowUser = async (id) => {
  try {
    const { data } = await apiClient.post(allowUser, { id });

    // Update localStorage if the current user is the one being allowed.
    // Display-only cache update — the server already has the real
    // `allowed` flag; this just keeps the cached object from looking
    // stale in this browser tab.
    const currentUserData = localStorage.getItem(process.env.REACT_APP_LOCALHOST_KEY);
    if (currentUserData) {
      const parsedUser = JSON.parse(currentUserData);

      if (parsedUser._id === id) {
        const updatedUser = {
          ...parsedUser,
          allowed: true,
        };
        localStorage.setItem(process.env.REACT_APP_LOCALHOST_KEY, JSON.stringify(updatedUser));
      }
    }

    setToastMessage({
      message: data.message,
      type: 'success'
    });
    setShowToast(true);
    fetchRequestedUsers();
  } catch (error) {
    console.error("Error allowing user:", error);
    setToastMessage({
      message: error.response?.data?.message || 'Error allowing user',
      type: 'error'
    });
    setShowToast(true);
  }
};

  // Handle reject user
  const handleRejectUser = async (id) => {
    try {
      const { data } = await apiClient.post(rejectUser, { id });
      setToastMessage({
        message: data.message,
        type: 'success'
      });
      setShowToast(true);
      fetchRequestedUsers();
    } catch (error) {
      console.error("Error rejecting user:", error);
      setToastMessage({
        message: error.response?.data?.message || 'Error rejecting user',
        type: 'error'
      });
      setShowToast(true);
    }
  };

  // NEW: bulk allow/reject — the backend has supported this since the
  // last pass (POST /bulk-update-status, admin + manage_users only)
  // but nothing in the UI ever called it. Wired up here as
  // checkboxes + two action buttons above the list.
  const toggleSelected = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((existing) => existing !== id) : [...prev, id]
    );
  };

  const toggleSelectAll = () => {
    setSelectedIds((prev) =>
      prev.length === requestedUsers.length ? [] : requestedUsers.map((u) => u._id)
    );
  };

  const handleBulkAction = async (status) => {
    if (selectedIds.length === 0 || bulkActionLoading) return;

    setBulkActionLoading(true);
    try {
      const { data } = await apiClient.post(bulkUpdateUserStatus, {
        userIds: selectedIds,
        status, // 'allow' | 'reject'
      });
      setToastMessage({ message: data.msg, type: 'success' });
      setShowToast(true);
      fetchRequestedUsers(userSearch, 1);
    } catch (error) {
      console.error("Error applying bulk action:", error);
      setToastMessage({
        message: error.response?.data?.msg || 'Error updating selected users',
        type: 'error',
      });
      setShowToast(true);
    } finally {
      setBulkActionLoading(false);
    }
  };

  // Handle request submission
  const handleRequestSubmit = async (e) => {
    e.preventDefault();

    const trimmedAddress = address.trim();

    if (!phoneNumber.match(/^\d{6,15}$/)) {
      setToastMessage({
        message: 'Please enter a valid phone number (6-15 digits).',
        type: 'error'
      });
      setShowToast(true);
      return;
    }

    if (trimmedAddress === "") {
      setToastMessage({
        message: 'Address cannot be empty.',
        type: 'error'
      });
      setShowToast(true);
      return;
    }

    if (!selectedCountry) {
      setToastMessage({
        message: 'Please select a country.',
        type: 'error'
      });
      setShowToast(true);
      return;
    }

    try {
      const { data } = await apiClient.post(requesttosell, {
        phoneNumber: `${phoneCode}${phoneNumber}`,
        address: trimmedAddress,
        userId,
        selectedCountry,
      });
      setToastMessage({
        message: data.message,
        type: 'success'
      });
      setShowToast(true);

      handleAllowUser(userId);
      fetchRequestedUsers();
      navigate(`/addproduct/${userId}`);
    } catch (error) {
      console.error("Error submitting request:", error);
      setToastMessage({
        message: error.response?.data?.message || 'Error submitting request',
        type: 'error'
      });
      setShowToast(true);
    }
  };

    useEffect(() => {
    if (showToast) {
      const timer = setTimeout(() => {
        setShowToast(false);
        setToastMessage(null);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [showToast]);

  // Fetch users on component mount (kept for any code path that
  // relies on requestedUsers being populated before isAdmin resolves;
  // the debounced effect above re-fetches once isAdmin is known too)
  useEffect(() => {
    fetchRequestedUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const canBulkManage = isAdmin && hasPermission('manage_users');

  return (
    <div className="user-requests-container">
    <Link to={'/'}><AiOutlineArrowLeft style={{ position: 'absolute', top: 8, left: 8, fontSize: 24, cursor: 'pointer' }} /></Link>

      <h1 className="page-title">Send Request</h1>
       {showToast && (
        <div style={{
          position: 'fixed',
          top: '20%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          backgroundColor: toastMessage?.type === 'error' ? 'rgba(220, 38, 38, 0.95)' : 'rgba(0, 0, 0, 0.85)',
          color: 'white',
          padding: '16px 24px',
          borderRadius: '8px',
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          zIndex: 1000,
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
          animation: 'fadeIn 0.3s ease-in-out',
          maxWidth: '90%',
          textAlign: 'center'
        }}>
          {toastMessage?.type === 'error' ? (
            <AiOutlineExclamationCircle size={24} color="#fef2f2" />
          ) : (
            <AiOutlineCheckCircle size={24} color="#4caf50" />
          )}
          <div>
            <div style={{ fontWeight: '600', marginBottom: '4px' }}>
              {toastMessage?.type === 'error' ? 'Error!' : 'Congratulations!'}
            </div>
            <div style={{ fontSize: '14px' }}>
              {toastMessage?.message} 
            </div>
          </div>
        </div>
      )}
      {roleLoading ? (
        <p style={{ textAlign: 'center' }}>Loading...</p>
      ) : isAdmin ? (
        <>
          <Link to={`/agents/${userId}`} className="agents-link">
            Agents
          </Link>
          <Link to={`/emails/${userId}`} className="agents-link">Emails</Link>
          <Link to={`/manageadmins/${userId}`} className="agents-link">Admins</Link>
          {/* Admin role management (grant/revoke by email) is a
              separate screen — see AdminManagement.js — linked from
              wherever your admin nav lives. */}
          {/* List of requested users */}
          <h2 className="requests-title">Requested Users</h2>
          <div className="search-container">
            <input
              type="text"
              placeholder="Search users by name, email, phone, location..."
              value={userSearch}
              onChange={(e) => setUserSearch(e.target.value)}
              className="search-input"
            />
          </div>

          {canBulkManage && requestedUsers.length > 0 && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                margin: '10px 0',
                flexWrap: 'wrap',
              }}
            >
              <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px' }}>
                <input
                  type="checkbox"
                  checked={selectedIds.length === requestedUsers.length}
                  onChange={toggleSelectAll}
                />
                Select all ({selectedIds.length}/{requestedUsers.length})
              </label>

              <button
                type="button"
                disabled={selectedIds.length === 0 || bulkActionLoading}
                onClick={() => handleBulkAction('allow')}
                style={{
                  padding: '6px 12px',
                  borderRadius: '6px',
                  border: 'none',
                  background: selectedIds.length === 0 ? '#a7f3d0' : '#059669',
                  color: 'white',
                  cursor: selectedIds.length === 0 ? 'default' : 'pointer',
                  fontSize: '13px',
                }}
              >
                Allow Selected
              </button>

              <button
                type="button"
                disabled={selectedIds.length === 0 || bulkActionLoading}
                onClick={() => handleBulkAction('reject')}
                style={{
                  padding: '6px 12px',
                  borderRadius: '6px',
                  border: 'none',
                  background: selectedIds.length === 0 ? '#fecaca' : '#dc2626',
                  color: 'white',
                  cursor: selectedIds.length === 0 ? 'default' : 'pointer',
                  fontSize: '13px',
                }}
              >
                Reject Selected
              </button>
            </div>
          )}

      {requestedUsers.length > 0 ? (
  <ul className="users-list">

    {requestedUsers.map((user) => (

      <li
        key={user._id}
        className="user-item"
      >

        <div className="user-info">

          {/* AVATAR */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              marginBottom: "10px",
            }}
          >
            {canBulkManage && (
              <input
                type="checkbox"
                checked={selectedIds.includes(user._id)}
                onChange={() => toggleSelected(user._id)}
                style={{ marginRight: '4px' }}
              />
            )}

            {user.isAvatarImageSet &&
            user.avatarImage ? (
              <img
                src={
                  user.avatarImage?.startsWith("http")
                    ? user.avatarImage // Google avatar / remote URL
                    : user.avatarImage?.startsWith("data:image")
                    ? user.avatarImage // already full base64 image
                    : `data:image/svg+xml;base64,${user.avatarImage}` // old stored avatar
                }
                alt={user.username}
                className="avatar"
              />            ) : (
              <div
                style={{
                  width: "50px",
                  height: "50px",
                  borderRadius: "50%",
                  background: "#ddd",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontWeight: "bold",
                }}
              >
                {user.username?.charAt(0)}
              </div>
            )}

            <div>
              <p>
                <strong>
                  Username:
                </strong>{" "}
                {user.username}
              </p>

              <p>
                <strong>
                  Email:
                </strong>{" "}
                {user.email}
              </p>
            </div>
          </div>

          {/* CONTACT */}
          <p>
            <strong>
              Phone:
            </strong>{" "}
            {user.phoneNumber ||
              "N/A"}
          </p>

          <p>
            <strong>
              Address:
            </strong>{" "}
            {user.address ||
              "N/A"}
          </p>

          <p>
            <strong>
              Country:
            </strong>{" "}
            {user.country ||
              "N/A"}
          </p>

          {/* SUBSCRIPTION */}
          <p>
            <strong>
              Subscription:
            </strong>{" "}
            {user.subscription?.type ||
              "none"}
          </p>

          {/* PROFILE COMPLETENESS */}
          <p>
            <strong>
              Profile:
            </strong>{" "}
            {
              user.profileCompleteness
            }
            %
          </p>

          {/* LAST ACTIVE */}
          {user.lastActive && (
            <p>
              <strong>
                Last Active:
              </strong>{" "}
              {new Date(
                user.lastActive
              ).toLocaleString()}
            </p>
          )}
        </div>

        {/* ACTION BUTTONS */}
        {!user.allowed ? (
          <button
            onClick={() =>
              handleAllowUser(
                user._id
              )
            }
            className="allow-btn"
          >
            Allow
          </button>
        ) : (
          <button
            onClick={() =>
              handleRejectUser(
                user._id
              )
            }
            className="reject-btn"
          >
            Reject
          </button>
        )}
      </li>
    ))}
  </ul>
) : (
  <p className="no-requests">
    {userSearch
      ? "No users match your search."
      : "No requests found."}
  </p>
)}
        </>
      ) : hasRequested ? (
        <div className="request-message">
          <p>You already requested to post products, kindly review our <Link to='/terms&conditions' style={{textDecoration: 'none' }}>
           terms and conditions </Link> and renew your subscription to be allowed to post again.</p>
           <p>For more info <Link to={`/chat/${ADMIN_USER_ID}`} style={{textDecoration: 'none' }}>contact </Link> support team</p>
        </div>
      ) : (
        /* Form for phone number and address */
        <form onSubmit={handleRequestSubmit} className="request-form">
          <div className="form-group">
            <p style={{display:'flex', justifyContent:'center'}}><strong>Register and Enjoy a One-Month Free Trial</strong></p>
            <label htmlFor="country">Country:</label>
            <div className="country-search-container">
              <input
                type="text"
                id="country"
                value={countrySearch}
                onChange={(e) => {
                  setCountrySearch(e.target.value);
                  setShowCountryDropdown(true);
                }}
                onFocus={() => setShowCountryDropdown(true)}
                placeholder="Search for a country..."
                required
                className="country-input"
              />
              {showCountryDropdown && filteredCountries.length > 0 && (
                <div className="country-dropdown">
                  {filteredCountries.map((country) => (
                    <div 
                      key={`${country.name}-${country.phoneCode}`} 
                      className="country-item"
                      onClick={() => handleCountrySelect(country)}
                    >
                      <span className="country-flag">{country.flag}</span>
                      <span className="country-name">{country.name}</span>
                      <span className="country-code">{country.phoneCode}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="phoneNumber">Phone Number:</label>
            <div className="phone-input-container">
              <span className="phone-code">{phoneCode || "Select country first"}</span>
              <input
                type="number"
                id="phoneNumber"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                required
                className="phone-input"
                placeholder="123456789"
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="address">Address (Region/Mkoa):</label>
            <input
              type="text"
              id="address"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              required
              className="address-input"
            />
          </div>

          <button type="submit" className="submit-btn">
            Submit Request
          </button>
        </form>
      )}

    </div>
  );
};

export default UserRequests;
